import React, { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { isFunction, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import Button from "@cayuse/shared-react.components.button";
import Icon from "@cayuse/shared-react.components.icon";

import styles from "./styles.css";


const TextFacet = ({
	text,
	query,
	onChange,
	onClick,
	onDelete,
	shouldFocus,
	facetAriaControls,
}) => {
	const facetLabelRef = useRef();
	const queryRef = useRef();
	let ariaAttributes = { "aria-label": `Filter on ${text}` };

	if (isNonEmptyString(facetAriaControls)) {
		ariaAttributes["aria-controls"] = facetAriaControls;
	}

	const reportChange = query => {
		if (isFunction(onChange)) {
			onChange({ query });
		}
	};

	const handleInput = event => {
		const query = event.currentTarget?.innerText || "";
		reportChange(query);
	};

	const handleDelete = useCallback(() => {
		if (isFunction(onDelete)) {
			onDelete();
		}
	}, [ onDelete ]);

	const focusQuery = () => {
		if (queryRef.current) {
			queryRef.current.focus();
		}
	};

	useEffect(() => {
		if (queryRef.current) {
			queryRef.current.innerText = query || "";
		}
	}, [ ]);

	useEffect(() => {
		const current = facetLabelRef.current;
		if (current) {
			let el = current.querySelector("[aria-label='Filter on Status']");
			if (el) {
				if(el.innerHTML === "" || el.innerHTML !== query) {
					if (/^['"].+['"]$/.test(query)) {
						el.innerHTML = query.replace(/['"]+/g, '');
					}
				}
			}


			const handleClick = event => {
				// Added event.target.parentNode.dataset?.component === "Icon" to handle user clicking on the path of the svg
				const isDelete = event.target.dataset?.component === "Icon" || event.target.parentNode.dataset?.component === "Icon";

				event.stopPropagation();

				if (isDelete) {
					handleDelete();
				} else if (queryRef.current) {
					focusQuery();
				}

				onClick();
			};

			current.addEventListener("click", handleClick);

			return () => {
				current.removeEventListener("click", handleClick);
			};
		}
	}, [ facetLabelRef, handleDelete, onClick, query ]);

	useEffect(() => {
		if (shouldFocus) {
			focusQuery();
		}
	}, [ shouldFocus ]);

	// keyboard handling
	const handleButtonKeyDown = evt => {
		const key = evt.key;

		switch (key) {
		case "Enter":
			evt.preventDefault();
			handleDelete();
			break;

		default:
			break;
		}
	};

	return (
		<div data-component="FacetedSearchBar\TextFacet" className={styles.facetLabelInput} ref={facetLabelRef}>

			<div>{ text }:</div>

			<div ref={queryRef}
				{...ariaAttributes}
				 contentEditable={true}
				 content={query || ""}
				 className={styles.input}
				 onInput={handleInput}
			></div>
			<Button ariaLabel={`Remove ${text} filter`} onKeyDown={handleButtonKeyDown} custom className={styles.deleteButton}>
				<Icon className={styles.icon} name="times" size="smaller" removeMargin />
			</Button>
		</div>
	);
};

TextFacet.defaultProps = {
	onClick: () => {}
};

TextFacet.propTypes = {
	text: PropTypes.string,
	query: PropTypes.string,
	onChange: PropTypes.func,
	onClick: PropTypes.func,
	onDelete: PropTypes.func,
	facetAriaControls: PropTypes.string
};

export default TextFacet;
